import * as _ from "lodash";
import { isEmpty } from "lodash";
import * as React from "react";
import DishAdder from "../components/DishAdder";
import HorizontalTagsListView from "../components/HorizontalTagsListView";
import CustomHeader, { ICustomHeaderInfo } from "../components/utils/CustomHeader";
import { IDietRecallFoodOptionValue } from "../models/IDietRecallMealItem";
import IMealItem from "../models/IMealItem";
import IDishCatalogueResponse from "../models/maverick/IDishCatalogueResponse";
import { AppPlaceholders, ORION_IMG_URL, SUB_SECTIONS_CONTEXT_KEY } from "../utils/AppConstants";
import {LOADING_STATE} from "../utils/AppUtils";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import ModelFactory from "../models/ModelFactory";

let customMealBuilderHeaderInfos: ICustomHeaderInfo[] = [
    {label: "mealName", display: "MEAL NAME"}
];

export let customMealBuilderComponentsHeaderInfos: ICustomHeaderInfo[] = [
    {label: "constituents", display: "CONSTITUENTS"}
];

export interface CustomMealBuilderContainerProps {
    mealSlot: string;
    addMeal: (meal: IMealItem) => void;
    createMeal: (meal: IMealItem) => Promise<IMealItem>;
    fetchStaticDishes: (input: string) => Promise<IDishCatalogueResponse[]>;
    previewMeal: (meal: IMealItem) => Promise<IMealItem>;
    suggestions: {[key: string]: any}
}

export interface CustomMealBuilderContainerState {
    showMealBuilder: boolean;
    meal: IMealItem;
    preview: IMealItem;
    previewError: string;
    mealSaveError: string;
    previewMealLoadingStatus: LOADING_STATE;
    mealSaveLoadingStatus: LOADING_STATE;
}

class CustomMealBuilderContainer extends React.Component<CustomMealBuilderContainerProps, CustomMealBuilderContainerState> {
    constructor(props: CustomMealBuilderContainerProps) {
        super(props);
        this.state = {
            showMealBuilder: false,
            meal: ModelFactory.createNewMealItem(),
            preview: null,
            mealSaveError: null,
            previewError: null,
            previewMealLoadingStatus: LOADING_STATE.NOT_INITIALIZED,
            mealSaveLoadingStatus: LOADING_STATE.NOT_INITIALIZED
        }
    }
    onTagRemove = (index: number) => {
        let updatedComponents = [...this.state.meal.components];
        updatedComponents.splice(index, 1);
        let updatedMeal = { ...this.state.meal, components: updatedComponents }
        this.setState({
            meal: updatedMeal
        });
    }
    generateDishTags = () => {
        let tags = !_.isEmpty(this.state.meal.components) ? this.state.meal.components.map(item => {return `${item.displayName} ${item.quantity !== null && !_.isEmpty(item.unit) ? `- ${item.quantity} ${item.unit}` : "" }` }) : [];
        return tags;
    }
    addDish = (dish: IDietRecallFoodOptionValue) => {
        let formattedDish = {
            dishId: dish.id,
            displayName: dish.name,
            quantity: dish.count,
            unit: dish.unit
        }
        let mealComponents = !_.isEmpty(this.state.meal.components) ? this.state.meal.components : [];
        let updatedMeal = { ...this.state.meal, components: [ ...mealComponents, formattedDish ] };
        this.setState({meal: updatedMeal})
    }
    saveMeal = () => {
        let meal = this.state.meal;
        meal.shortDescription = "-";
        meal.detailedDescription = "-";
        meal.status = "UNAPPROVED";
        meal.source = "DOCSAPP";
        if ( meal.version !== undefined ) delete meal.version;
        meal.mealSlots = [this.props.mealSlot];
        this.setState({
            mealSaveLoadingStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.createMeal(this.state.meal).then((newMeal) => {
                this.props.addMeal(newMeal);
                LocalStorageUtils.addMealToMap(newMeal)
                this.hideMealBuilder();
            }).catch((err) => {
                this.setState({
                    mealSaveError: "Could not save meals",
                    mealSaveLoadingStatus: LOADING_STATE.LOADED
                })
            })
        })
    }
    hideMealBuilder = () => {
        this.setState({
            meal: ModelFactory.createNewMealItem(),
            showMealBuilder: false,
            previewMealLoadingStatus: LOADING_STATE.NOT_INITIALIZED,
            mealSaveLoadingStatus: LOADING_STATE.NOT_INITIALIZED,
            mealSaveError: null,
            previewError: null,
            preview: null
        })
    }
    previewMeal = () => {
        let meal = {...this.state.meal};
        meal.version = 0;
        this.setState({
            previewMealLoadingStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.previewMeal(meal).then((meal) => {
                this.setState({
                    preview: meal,
                    previewMealLoadingStatus: LOADING_STATE.LOADED
                })
            }).catch((err) => {
                this.setState({
                    previewMealLoadingStatus: LOADING_STATE.LOADED
                })
            });
        })
    }
    hideMealPreviewContainer = () => {
        this.setState({
            preview: null,
            previewError: null,
            previewMealLoadingStatus: LOADING_STATE.NOT_INITIALIZED
        })
    }
    hideMealSaveErrorContainer = () => {
        this.setState({
            mealSaveLoadingStatus: LOADING_STATE.NOT_INITIALIZED,
            mealSaveError: null
        })
    }

    getSuggestions = () => {
        let updatedDish = this.state.meal.components;
        let mealSlot = this.props.mealSlot.toLowerCase();
        if (!_.isEmpty(this.props.suggestions) && !_.isEmpty(this.props.suggestions[`meals-${mealSlot}`])) {
            const suggestions = this.props.suggestions[`meals-${mealSlot}`];
            if (updatedDish && updatedDish.length) {
                const alreadyFilledItems = updatedDish.map(dish => { return dish.displayName });
                return suggestions.filter((item: any) => !_.isEmpty(alreadyFilledItems) && alreadyFilledItems.indexOf(item.recommendation.displayName) < 0);
            }
            return suggestions;
        }
        return [];
    }

    render() {
        let isMealLoading = this.state.mealSaveLoadingStatus === LOADING_STATE.LOADING;
        let saveMealButtonText = isMealLoading ? "Saving..." : "Save";
        return (
            <div className={"custom-meal-builder-container"}>
                {
                    this.state.showMealBuilder &&
                        <div className={"custom-meal-builder-form"}>
                            <div className={"save-button collapsible-card"}>
                                <input className="card-primary-action" type="button" value={"Cancel"} onClick={this.hideMealBuilder} />
                                <input className="card-primary-action" type="button" value={"Preview"} onClick={this.previewMeal} />
                                <input disabled={_.isEmpty(this.state.meal.components) || isMealLoading} className="card-primary-action" type="button" value={saveMealButtonText} onClick={this.saveMeal} />
                            </div>
                            <h3 className={"brandon-bold"}>Meal Adder</h3>
                            {
                                this.state.mealSaveLoadingStatus === LOADING_STATE.LOADED && !_.isNull(this.state.mealSaveError) &&
                                <div className={"meal-save-error-info-container"}>
                                    <span className={"glyphicon glyphicon-remove"} onClick={this.hideMealSaveErrorContainer}></span>
                                    <div className={"meal-save-error-info"}>
                                        Could not save meal.
                                    </div>
                                </div>
                            }
                            {
                                this.state.previewMealLoadingStatus !== LOADING_STATE.NOT_INITIALIZED &&
                                <div className={"meal-preview"}>
                                    {
                                        this.state.previewMealLoadingStatus === LOADING_STATE.LOADING &&
                                        <div>
                                            <img className={"loader"} src={ORION_IMG_URL + "loader/rolling-loader.gif"} />
                                            Previewing Meal...
                                        </div>
                                    }
                                    {
                                        this.state.previewMealLoadingStatus === LOADING_STATE.LOADED && !_.isNull(this.state.preview) &&
                                        <div className={"nutrition-info-container"}>
                                            <span className={"glyphicon glyphicon-remove"} onClick={this.hideMealPreviewContainer}></span>
                                            <div className={"meal-preview-header brandon-bold"}>MEAL PREVIEW</div>
                                            {
                                                _.map(this.state.preview.nutritionInfo, (nutrition, key) => {
                                                    if (typeof nutrition !== "object") {
                                                        return <div className={"nutrition-info"}>
                                                            <span className={"nutrition-info-label brandon-bold"}>{key}</span>
                                                            <span className={"nutrition-info-value"}>{parseInt(nutrition.toString())}</span>
                                                        </div>
                                                    }
                                                })
                                            }
                                        </div>
                                    }
                                    {
                                        this.state.previewMealLoadingStatus === LOADING_STATE.LOADED && _.isNull(this.state.preview) &&
                                        <div className={"nutrition-info-error-container"}>
                                            Could not preview meal. ( Could not identify the dishes )
                                        </div>
                                    }
                                </div>
                            }
                            <CustomHeader headerInfos={customMealBuilderComponentsHeaderInfos} rootCls={"custom-meal-builder-components"}/>
                            <div className={"food-options-tags-container"}>
                                <HorizontalTagsListView tagsStringArray={this.generateDishTags()} onTagRemove={this.onTagRemove}/>
                            </div>
                            <DishAdder onDishAdd={this.addDish} fetchStaticDishes={this.props.fetchStaticDishes} suggestions={this.getSuggestions()} mealSlot={this.props.mealSlot}/>
                        </div>
                }
                {
                    !this.state.showMealBuilder &&
                    <span className={"add-meal-button brandon-bold"} onClick={() => this.setState({showMealBuilder: true})}>+ Add Meal</span>
                }
            </div>
        )
    }
}

export default CustomMealBuilderContainer;